import { Extension } from '@tiptap/core';
import { Fragment, Node as PmNode, Schema } from '@tiptap/pm/model';
import { Plugin, PluginKey, Transaction } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';

export const columnDropPluginKey = new PluginKey('columnDrop');

interface ColumnDropState {
  targetPos: number | null;
  side: 'left' | 'right' | null;
  active: boolean;
}

const initialState: ColumnDropState = {
  targetPos: null,
  side: null,
  active: false,
};

/**
 * Finds a valid drop-target block at the given document position.
 * Returns top-level blocks (direct children of doc), excluding columnLayout nodes.
 * Also returns blocks inside columns so users can drag beside them.
 */
function findDropTargetBlock(
  doc: PmNode,
  pos: number,
): { pos: number; node: PmNode } | null {
  if (pos < 0 || pos >= doc.content.size) return null;

  const $pos = doc.resolve(pos);

  for (let depth = $pos.depth; depth >= 1; depth--) {
    const node = $pos.node(depth);
    const parent = $pos.node(depth - 1);

    // Direct child of doc (top-level block)
    if (parent.type.name === 'doc') {
      // Skip columnLayout — can't drop beside it to nest
      if (node.type.name === 'columnLayout') return null;
      return { pos: $pos.before(depth), node };
    }

    // Block inside a column — valid drop target
    if (parent.type.name === 'column') {
      return { pos: $pos.before(depth), node };
    }
  }

  return null;
}

/**
 * Builds a transaction that wraps two blocks into a columnLayout.
 * The dragged block is removed from its original position and placed
 * beside the target block inside a new columnLayout node.
 */
function createColumnLayout(
  tr: Transaction,
  schema: Schema,
  draggedPos: number,
  draggedNode: PmNode,
  targetPos: number,
  targetNode: PmNode,
  side: 'left' | 'right',
): Transaction {
  const columnType = schema.nodes.column;
  const columnLayoutType = schema.nodes.columnLayout;

  if (!columnType || !columnLayoutType) return tr;

  // Determine which block goes left vs right
  const leftNode = side === 'left' ? draggedNode : targetNode;
  const rightNode = side === 'left' ? targetNode : draggedNode;

  const leftColumn = columnType.create(null, Fragment.from(leftNode));
  const rightColumn = columnType.create(null, Fragment.from(rightNode));
  const columnLayout = columnLayoutType.create(
    null,
    Fragment.from([leftColumn, rightColumn]),
  );

  // Process later position first to keep earlier positions valid
  if (draggedPos > targetPos) {
    tr.delete(draggedPos, draggedPos + draggedNode.nodeSize);
    tr.replaceWith(targetPos, targetPos + targetNode.nodeSize, columnLayout);
  } else {
    tr.replaceWith(targetPos, targetPos + targetNode.nodeSize, columnLayout);
    tr.delete(draggedPos, draggedPos + draggedNode.nodeSize);
  }

  return tr;
}

const ColumnDropPlugin = Extension.create({
  name: 'columnDropPlugin',

  addProseMirrorPlugins() {
    return [
      new Plugin<ColumnDropState>({
        key: columnDropPluginKey,

        state: {
          init(): ColumnDropState {
            return { ...initialState };
          },
          apply(tr, value): ColumnDropState {
            const meta = tr.getMeta(columnDropPluginKey);
            if (meta) return meta;
            return value;
          },
        },

        props: {
          decorations(state) {
            const pluginState = columnDropPluginKey.getState(state);
            if (
              !pluginState?.active ||
              pluginState.targetPos === null ||
              pluginState.side === null
            ) {
              return DecorationSet.empty;
            }

            const { targetPos, side } = pluginState;
            const node = state.doc.nodeAt(targetPos);
            if (!node) return DecorationSet.empty;

            const className =
              side === 'left'
                ? 'column-drop-indicator-left'
                : 'column-drop-indicator-right';

            return DecorationSet.create(state.doc, [
              Decoration.node(targetPos, targetPos + node.nodeSize, {
                class: className,
              }),
            ]);
          },

          handleDOMEvents: {
            dragover(view, event) {
              const { clientX, clientY } = event;

              const posInfo = view.posAtCoords({
                left: clientX,
                top: clientY,
              });
              if (!posInfo) {
                return false;
              }


              const blockInfo = findDropTargetBlock(
                view.state.doc,
                posInfo.pos,
              );
              if (!blockInfo || !blockInfo.node) {
                const current = columnDropPluginKey.getState(view.state);
                if (current?.active) {
                  const tr = view.state.tr.setMeta(
                    columnDropPluginKey,
                    initialState,
                  );
                  view.dispatch(tr);
                }
                return false;
              }


              const { pos: targetPos } = blockInfo;

              const domNode = view.nodeDOM(targetPos);
              if (!domNode || !(domNode instanceof HTMLElement)) {
                return false;
              }

              const rect = domNode.getBoundingClientRect();
              const relativeX = clientX - rect.left;
              const threshold = rect.width * 0.3;

              let side: 'left' | 'right' | null = null;
              if (relativeX < threshold) {
                side = 'left';
              } else if (relativeX > rect.width - threshold) {
                side = 'right';
              }

              if (side) {
                event.preventDefault();
                const tr = view.state.tr.setMeta(columnDropPluginKey, {
                  targetPos,
                  side,
                  active: true,
                } satisfies ColumnDropState);
                view.dispatch(tr);
                return true;
              } else {
                const current = columnDropPluginKey.getState(view.state);
                if (current?.active) {
                  const tr = view.state.tr.setMeta(
                    columnDropPluginKey,
                    initialState,
                  );
                  view.dispatch(tr);
                }
                return false;
              }
            },

            dragleave(view) {
              const current = columnDropPluginKey.getState(view.state);
              if (current?.active) {
                const tr = view.state.tr.setMeta(
                  columnDropPluginKey,
                  initialState,
                );
                view.dispatch(tr);
              }
              return false;
            },

            drop(view, event) {
              const pluginState = columnDropPluginKey.getState(view.state);
              if (
                !pluginState?.active ||
                pluginState.targetPos === null ||
                pluginState.side === null
              ) {
                return false;
              }

              event.preventDefault();

              const { targetPos, side } = pluginState;
              const targetNode = view.state.doc.nodeAt(targetPos);
              if (!targetNode) return false;

              // Get the dragged content from ProseMirror's internal drag state
              const dragging = view.dragging;
              if (!dragging?.slice) return false;

              const draggedNode = dragging.slice.content.firstChild;
              if (!draggedNode) return false;

              // Find the position of the dragged node in the document
              // We need to search for it since ProseMirror doesn't expose the source position directly
              let draggedPos: number | null = null;
              view.state.doc.descendants((node, pos) => {
                if (draggedPos !== null) return false;
                if (node.eq(draggedNode) && pos !== targetPos) {
                  draggedPos = pos;
                  return false;
                }
              });

              if (draggedPos === null) return false;

              // Don't allow dropping a block beside itself
              if (draggedPos === targetPos) return false;

              const { tr } = view.state;

              // Clear the drop indicator
              tr.setMeta(columnDropPluginKey, initialState);

              // Create the column layout
              createColumnLayout(
                tr,
                view.state.schema,
                draggedPos,
                draggedNode,
                targetPos,
                targetNode,
                side,
              );

              view.dispatch(tr);
              return true;
            },
          },
        },
      }),
    ];
  },
});

export default ColumnDropPlugin;
